import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { updateProduct, deleteProduct } from "../../features/freezerSlice";
import { validateProduct, CATEGORIES } from "../../features/validationUtils";
import styles from "../addProductModal/addProductModal.module.scss";

const EditProductModal = ({ product, onClose }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: product.name,
    quantity: product.quantity,
    category: product.category,
    containerId: product.containerId,
    details: {
      weight: product.details?.weight || 0,
      portions: product.details?.portions || 1,
      additionalDetails: product.details?.additionalDetails || "",
      freezeDate: product.details?.freezeDate,
      dlc: product.details?.dlc,
    },
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const categoryOptions = Object.entries(CATEGORIES).map(([id, label]) => ({
    id,
    label,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (
      name === "weight" ||
      name === "portions" ||
      name === "additionalDetails"
    ) {
      setFormData((prev) => ({
        ...prev,
        details: {
          ...prev.details,
          [name]:
            name === "weight" || name === "portions" ? Number(value) : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: name === "quantity" ? Number(value) : value,
      }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validation simplifiée pour l'édition
    const validationErrors = {};
    if (!formData.name.trim()) {
      validationErrors.name = "Le libellé est requis";
    }
    if (!formData.quantity || formData.quantity < 1) {
      validationErrors.quantity = "Le nombre d'unités doit être au moins 1";
    }
    if (!formData.category) {
      validationErrors.category = "La catégorie est requise";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await dispatch(
        updateProduct({
          productId: product._id,
          updates: formData,
        })
      ).unwrap();
      onClose();
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit:
          error.message || "Une erreur est survenue lors de la modification",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReduceUnits = async () => {
    if (formData.quantity > 1) {
      setFormData((prev) => ({
        ...prev,
        quantity: prev.quantity - 1,
      }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDelete = async () => {
    if (window.confirm("Es tu sûr.e. de vouloir supprimer ce produit ?")) {
      setIsDeleting(true);
      try {
        await dispatch(deleteProduct(product._id)).unwrap();
        onClose();
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          submit:
            error.message || "Une erreur est survenue lors de la suppression",
        }));
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2>Modifier un produit</h2>

        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="name">Libellé *</label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? styles.errorInput : ""}
            />
            {errors.name && (
              <span className={styles.errorText}>{errors.name}</span>
            )}
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="quantity">Unités *</label>
              <div className={styles.unitsControl}>
                <input
                  id="quantity"
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="1"
                  className={errors.quantity ? styles.errorInput : ""}
                />
                <button
                  type="button"
                  onClick={handleReduceUnits}
                  disabled={formData.quantity <= 1}
                  className={styles.reduceButton}
                >
                  -1 unité
                </button>
              </div>
              {errors.quantity && (
                <span className={styles.errorText}>{errors.quantity}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="weight">Poids (g)</label>
              <input
                id="weight"
                type="number"
                name="weight"
                value={formData.details.weight}
                onChange={handleChange}
                min="0"
                className={errors.weight ? styles.errorInput : ""}
              />
              {errors.weight && (
                <span className={styles.errorText}>{errors.weight}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="portions">Portions</label>
              <input
                id="portions"
                type="number"
                name="portions"
                value={formData.details.portions}
                onChange={handleChange}
                min="1"
                className={errors.portions ? styles.errorInput : ""}
              />
              {errors.portions && (
                <span className={styles.errorText}>{errors.portions}</span>
              )}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Date de congélation</label>
              <div className={styles.readOnlyField}>
                {formatDate(formData.details.freezeDate)}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>DLC</label>
              <div className={styles.readOnlyField}>
                {formatDate(formData.details.dlc)}
              </div>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="additionalDetails">Détails</label>
            <textarea
              id="additionalDetails"
              name="additionalDetails"
              value={formData.details.additionalDetails}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="category">Catégorie *</label>
            <div className={styles.categoryButtons}>
              {categoryOptions.map((cat) => (
                <label
                  key={cat.id}
                  className={`${styles.categoryLabel} ${
                    formData.category === cat.id ? styles.selected : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.id}
                    checked={formData.category === cat.id}
                    onChange={handleChange}
                  />
                  {cat.label}
                </label>
              ))}
            </div>
            {errors.category && (
              <span className={styles.errorText}>{errors.category}</span>
            )}
          </div>

          {errors.submit && (
            <div className={styles.submitError}>{errors.submit}</div>
          )}

          <div className={styles.buttonGroup}>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className={styles.deleteButton}
            >
              {isDeleting ? "Suppression..." : "Supprimer"}
            </button>
            <div className={styles.rightButtons}>
              <button
                type="button"
                onClick={onClose}
                className={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={styles.submitButton}
              >
                {isSubmitting ? "Modification..." : "Modifier"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProductModal;
